import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '@env';
import { _getEnv, _isFeDev, _log, _logTap, _useDummyData, _warn } from '@shared/aux_helper_environment';
import {
  _getNewNumberId,
  _throwError,
  _timeout,
  _dateUtcToLocal,
  _prettyDateEs,
  _parese_and_normalize_notification_body_msg,
} from '@shared/aux_helper_functions';
import { Subject } from 'rxjs';
import { tmpNotifications } from './notifications-panel.auxs';
import { NotificationModel } from './notifications-panel.model';
import { catchError, tap, map, first, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { _debounceDecorator } from '@shared/aux_helper_functions';
import { ToastrService } from 'ngx-toastr';

const TOAST_DURATION = _getEnv('notifications.toastDuration', 5000);
const FORCE_DATE_GMT0 = _getEnv('notifications.forceDatesTogmt0', false);

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private items$ = new Subject<NotificationModel>();
  private suggestionsGenerationCompleted$ = new Subject<void>();
  /** Evita doble señal: toast en vivo + refresco desde BE para la misma generación (y 2× getDetail en Catman). */
  private lastSuggestionsGenerationSignalMs = 0;
  private readonly suggestionsGenerationSignalDebounceMs = 1800;
  private hasHydratedFromBE = false;
  private knownNotificationIds = new Set<number>();
  private knownNotificationKeys = new Set<string>();

  private config = environment.notification.api;
  private configBase = environment.apiBaseUrl_notification;

  constructor(private snackBar: MatSnackBar, private http: HttpClient, private toastr: ToastrService) {
    if (true) {
      (window as any).__testNewNotif = () => this.testNewNotif();
    }
  }

  date2dateago(date: any, format = true): string {
    const dateStr = _prettyDateEs(new Date(date || ''));
    return format ? dateStr.charAt(0).toUpperCase() + dateStr.slice(1) + '.' : dateStr;
  }

  async setNewItem(payLoad) {
    if (payLoad && payLoad.title) {
      const note = payLoad;

      note.originalID = note.id;

      if (!note.id) {
        note.id = _getNewNumberId(false);
      }

      const urlToDownload = note.urlToDownload || null;
      const urlToGo = urlToDownload ? null : note.urlToGo || null;

      if (!note.detail && note.body) {
        note.detail = _parese_and_normalize_notification_body_msg(note.body, urlToDownload, urlToGo);
      }

      if (!note.largeDetail && note.body) {
        note.largeDetail = _parese_and_normalize_notification_body_msg(note.body, urlToDownload, urlToGo);
      }

      if (!note.date) {
        note.date = new Date();
      }

      note.date = new Date(note.date);

      note.title = _parese_and_normalize_notification_body_msg(note.title, null, null);

      note._isFireBase = true;

      _log('🔔 [toastr notification]', note);

      const hasUrl = _parese_and_normalize_notification_body_msg(payLoad.body, urlToDownload, urlToGo)?.includes?.('linkInNotification');

      const titleLower = String(note.title || '').toLowerCase();
      const bodyLower = String(note.body || '').toLowerCase();
      const isSuggestionsToast = titleLower.includes('sugerencias') || bodyLower.includes('sugerencias');
      const isSuggestionsGeneratedToast =
        isSuggestionsToast &&
        (titleLower.includes('generad') ||
          titleLower.includes('completad') ||
          bodyLower.includes('completad') ||
          bodyLower.includes('ver sugerencias'));
      if (isSuggestionsGeneratedToast) {
        const id = Number((note as any)?.id);
        if (Number.isFinite(id) && id > 0) {
          this.knownNotificationIds.add(id);
        }
        const sig = this.buildNotificationSignature(note as any);
        if (sig) {
          this.knownNotificationKeys.add(sig);
        }
        this.notifySuggestionsGenerationCompleted();
      }
      const toastTimeOut = isSuggestionsGeneratedToast ? 0 : (_isFeDev() && false) || hasUrl ? TOAST_DURATION * 100 : TOAST_DURATION;
      const extraToastClass = [payLoad.toastClass, isSuggestionsToast ? 'ngx-toastr-suggestions' : ''].filter(Boolean).join(' ');
      const toastClass = `ngx-toastr ngx-toastr-notif ${
        !(payLoad.fail || payLoad.error) ? 'ngx-toastr-notifOK' : 'ngx-toastr-notifFAIL'
      } ${extraToastClass}`.trim();

      /*NEW NOTIF // https://github.com/scttcper/ngx-toastr#iconclasses-defaults */
      this.toastr
        .success(
          `${_parese_and_normalize_notification_body_msg(payLoad.body)}`,
          _parese_and_normalize_notification_body_msg(payLoad.title, null),
          {
            timeOut: toastTimeOut,
            extendedTimeOut: toastTimeOut,
            disableTimeOut: isSuggestionsGeneratedToast,
            closeButton: true,
            enableHtml: true,
            tapToDismiss: !isSuggestionsToast,
            toastClass,
            positionClass: 'toast-top-right',
            easeTime: 300,
          }
        )
        .onTap.pipe(take(1))
        .subscribe(() => this.toasterClickedHandler(note));

      // TODO: En lugar de agregarlo a items nuevos, volver a refrescar los de la DB
      await _timeout(512);
      this.setNotifItemsFromBE();
    } else {
      _warn('🔔 [toastr bad payLoad]', payLoad);
    }
  }

  toasterClickedHandler(note) {
    if (note && note.originalID && note.id) {
      /*Si la notif vino con id desde FB se marca como leída al clickear*/
      this.clearItem(note);
    }
  }

  @_debounceDecorator(512)
  public async setNotifItemsFromBE() {
    this.items$.next(null);

    if (false && _useDummyData()) {
      await _timeout(500);
      this.items$.next(tmpNotifications as NotificationModel);
      return;
    }

    const warnNoNotif = error => {
      if (true) _warn('[NotificationService fail getItemsFromServer]', error);
      this.items$.next([]);
    };

    this.getItemsFromServer()
      .pipe(first())
      .subscribe(data => {
        if (data && Array.isArray(data)) {
          if (false) _log('[NotificationService getItemsFromServer]', data);
          this.showNewCompletedSuggestionsToasts(data as NotificationModel);
          this.items$.next(data as NotificationModel);
        } else {
          warnNoNotif(data);
        }
      }, warnNoNotif);
  }

  private showNewCompletedSuggestionsToasts(data: NotificationModel): void {
    const isFirstHydration = !this.hasHydratedFromBE;
    for (const note of data || []) {
      const signature = this.buildNotificationSignature(note);
      const id = Number((note as any)?.id);

      // En la primera carga solo hidratar memoria; nunca mostrar toasts históricos.
      if (isFirstHydration) {
        if (Number.isFinite(id) && id > 0) this.knownNotificationIds.add(id);
        this.knownNotificationKeys.add(signature);
        continue;
      }

      // Dedupe por id (si existe) y por firma de contenido (fallback robusto).
      if (Number.isFinite(id) && id > 0) {
        if (this.knownNotificationIds.has(id)) continue;
        this.knownNotificationIds.add(id);
      }
      if (this.knownNotificationKeys.has(signature)) continue;
      this.knownNotificationKeys.add(signature);

      const title = String((note as any)?.title || '').toLowerCase();
      const detail = String((note as any)?.detail || (note as any)?.message || '').toLowerCase();
      const isCompletedSuggestions =
        (title.includes('sugerencias') || detail.includes('sugerencias')) &&
        (title.includes('completad') || detail.includes('completad') || detail.includes('ver sugerencias'));
      if (!isCompletedSuggestions) continue;

      const toastClass = `ngx-toastr ngx-toastr-notif ngx-toastr-notifOK ngx-toastr-suggestions ngx-toastr-suggestions-complete`;
      const toastBody = this.buildCompletedSuggestionsToastBody(note);
      this.toastr
        .success(toastBody, String((note as any)?.title || ''), {
          timeOut: 0,
          extendedTimeOut: 0,
          disableTimeOut: true,
          closeButton: true,
          enableHtml: true,
          tapToDismiss: false,
          toastClass,
          positionClass: 'toast-top-right',
          easeTime: 300,
        })
        .onTap.pipe(take(1))
        .subscribe(() => this.toasterClickedHandler(note));

      this.notifySuggestionsGenerationCompleted();
    }
    this.hasHydratedFromBE = true;
  }

  private buildCompletedSuggestionsToastBody(note: any): string {
    const rawDetail = String(note?.detail || note?.message || '');
    const cleanDetail = rawDetail.replace(/<a\b[^>]*>(.*?)<\/a>/gi, '$1');
    const lines = cleanDetail
      .replace(/<br\s*\/?>/gi, '\n')
      .split('\n')
      .map(x => x.trim())
      .filter(Boolean);
    const summaryText = 'Ver sugerencias aqui';
    const filteredDetailLines = lines.filter(line => {
      const l = line.toLowerCase();
      if (l.includes('finaliz')) return false;
      if (l.includes('registros afectados')) return false;
      if (l.includes('refresco de pantalla')) return false;
      return true;
    });
    const detailHtml = filteredDetailLines.join('<br/>');
    const firstLinkMatch = rawDetail.match(/<a\b[^>]*href=["']([^"']+)["'][^>]*>/i);
    const url = firstLinkMatch?.[1] || String(note?.urlToGo || '');
    const summary = url ? `<a class="linkInNotification" href="${url}">${summaryText}</a>` : `<span>${summaryText}</span>`;

    return `
      <div class="suggestions-toast-card suggestions-toast-card--complete">
        <details class="suggestions-toast-details">
          <summary class="suggestions-toast-summary">
            ${summary}
            <span class="suggestions-toast-chevron">▾</span>
          </summary>
          <div class="suggestions-toast-card__content">
            <div class="suggestions-toast-card__divider"></div>
            <div class="suggestions-toast-card__criteria">${detailHtml}</div>
          </div>
        </details>
      </div>
    `;
  }

  private buildNotificationSignature(note: any): string {
    const title = String(note?.title || '')
      .trim()
      .toLowerCase();
    const detail = String(note?.detail || note?.message || '')
      .trim()
      .toLowerCase();
    const rawDate = note?.creationDate || note?.date || '';
    const dateIso = rawDate ? new Date(rawDate).toISOString() : '';
    return `${title}|${detail}|${dateIso}`;
  }

  private getItemsFromServer() {
    const url = `${this.configBase}${this.config.resources.getNotifications}`;

    return this.http.get<NotificationModel>(url).pipe(
      tap(data => _logTap(`[${NotificationService.name}] getItemsFromServer (tap) / data: size: `, data?.length)),
      map(data => {
        data?.forEach?.($obj => {
          if (!$obj.id) return;
          let obj = $obj as any;

          obj._readed = obj.isCleared;
          obj.date = obj.date || FORCE_DATE_GMT0 ? _dateUtcToLocal(obj.creationDate) : obj.creationDate || new Date();
          obj._isFireBase = false;

          if (false && _isFeDev()) {
            obj.urlToDownload =
              'https://iantechdevstorage.blob.core.windows.net/megademolab/Development/Pricing/reports/PosItemsDataWithAssortment/2023-09-21_15-05-11-PosItemsDataReport.xlsx';
          }

          //DES-4382
          obj.detail = _parese_and_normalize_notification_body_msg(obj.message, obj?.urlToDownload || null, obj?.urlToGo || null);

          obj.title = _parese_and_normalize_notification_body_msg(obj.title, null);

          return obj;
        });
        return data;
      }),
      catchError(error => _throwError(error, NotificationService))
    );
  }

  public async clearItem(item) {
    if (!item || !item.id) return;

    const url = `${this.configBase}${this.config.resources.clear}?notificationId=${item.id}`;

    if (false && _useDummyData()) {
      this.items$.next(null);
      await _timeout(500);
      this.items$.next(tmpNotifications as NotificationModel);
      return;
    }

    return this.http
      .post<any>(url, null)
      .pipe(
        first(),
        tap(data => _logTap(`${NotificationService.name}::clearItem (tap)\n\tdata: %o`, data)),
        catchError(error => _throwError(error, NotificationService))
      )
      .subscribe(d => {
        this.setNotifItemsFromBE();
      });
  }

  public async clearAllItems() {
    const url = `${this.configBase}${this.config.resources.clearAll}`;

    return this.http
      .post<any>(url, null)
      .pipe(
        first(),
        tap(data => _logTap(`${NotificationService.name}::clearAllItem (tap)\n\tdata: %o`, data)),
        catchError(error => _throwError(error, NotificationService))
      )
      .subscribe(d => {
        this.setNotifItemsFromBE();
      });
  }

  getItems() {
    return this.items$;
  }

  getSuggestionsGenerationCompleted() {
    return this.suggestionsGenerationCompleted$;
  }

  /**
   * Una misma generación puede disparar: (1) push/toast inmediato y (2) la notif al hidratar desde BE.
   * Dedupa por id/firma en los bucles + ventana de tiempo para que el detalle no recargue dos veces.
   */
  private notifySuggestionsGenerationCompleted(): void {
    const now = Date.now();
    if (now - this.lastSuggestionsGenerationSignalMs < this.suggestionsGenerationSignalDebounceMs) {
      return;
    }
    this.lastSuggestionsGenerationSignalMs = now;
    this.suggestionsGenerationCompleted$.next();
  }

  private testNewNotif() {
    // this.setNewItem({ title: 'Notificación Nueva', body: 'Body de \n la notificación ' + (new Date() as any).toGMTString() });

    this.setNewItem({
      title: 'Notificación Nueva',
      body: "{'message':'Descargue el reporte de la tienda ST100','urlToDownload':'https://iantechdevstorage.blob.core.windows.net/megademolab/Development/Pricing/reports/PosItemsDataWithAssortment/2023-09-21_15-05-11-PosItemsDataReport.xlsx'}",
    });
  }
}
